import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe2, Plus } from "lucide-react";
import { useState } from "react";
import { ApiError } from "@hostdock/sdk";
import type { RegionRecord } from "@hostdock/types";
import { ActionMenu, Badge, Button, DataTable, Dialog, EmptyState, Field, FormError, Input, PageHeader, Panel, Select, StatusBadge, toast } from "@hostdock/ui";
import { api } from "../../api";
import { RequirePermission } from "../../lib/guard";
import { useSession } from "../../session";

const pct = (v: number | null) => (v === null ? "—" : `${v.toFixed(0)}%`);

export function RegionsPage() {
  return (
    <RequirePermission anyOf={["INFRASTRUCTURE_VIEW"]}>
      <Regions />
    </RequirePermission>
  );
}

function Regions() {
  const { can } = useSession();
  const q = useQuery({ queryKey: ["regions"], queryFn: api.regions.list, refetchInterval: 30_000 });
  const [editing, setEditing] = useState<RegionRecord | "new" | null>(null);
  return (
    <>
      <PageHeader
        title="Regions"
        description="Hosting locations. Every server belongs to exactly one region; customers choose a region when creating a website."
        actions={can("SERVER_MANAGE") && <Button variant="primary" icon={<Plus size={15} />} onClick={() => setEditing("new")}>New region</Button>}
      />
      <Panel flush>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(r) => r.id}
          empty={<EmptyState icon={Globe2} title="No regions yet" description="Create a region (for example Bangladesh / BDIX or USA) before adding servers." />}
          columns={[
            {
              key: "name",
              header: "Region",
              cell: (r) => (
                <div>
                  <p className="flex items-center gap-2 font-medium">{r.name} <Badge>{r.code}</Badge></p>
                  {r.location && <p className="text-[12px] text-muted">{r.location}</p>}
                </div>
              ),
            },
            { key: "status", header: "Status", cell: (r) => <StatusBadge status={r.status} /> },
            { key: "nodes", header: "Nodes", cell: (r) => <span className="tabular-nums">{r.stats.servers}</span> },
            { key: "healthy", header: "Healthy", cell: (r) => <span className="tabular-nums">{r.stats.healthy}{r.stats.warning ? <span className="text-warning"> · {r.stats.warning} warn</span> : null}{r.stats.offline ? <span className="text-danger"> · {r.stats.offline} off</span> : null}</span> },
            { key: "cpu", header: "CPU", cell: (r) => <span className="tabular-nums">{pct(r.stats.cpuPercent)}</span>, hideOnMobile: true },
            { key: "ram", header: "RAM", cell: (r) => <span className="tabular-nums">{pct(r.stats.memPercent)}</span>, hideOnMobile: true },
            { key: "disk", header: "Storage", cell: (r) => <span className="tabular-nums">{pct(r.stats.diskPercent)}</span>, hideOnMobile: true },
            {
              key: "actions",
              header: "",
              className: "w-10 text-right",
              cell: (r) => <ActionMenu actions={[{ label: "Edit", onSelect: () => setEditing(r), hidden: !can("SERVER_MANAGE") }]} />,
            },
          ]}
        />
      </Panel>
      {editing && <RegionDialog region={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function RegionDialog({ region, onClose }: { region: RegionRecord | null; onClose(): void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ code: region?.code ?? "", name: region?.name ?? "", location: region?.location ?? "", status: region?.status ?? "ACTIVE" });
  const save = useMutation({ meta: { inlineError: true },
    mutationFn: () => (region ? api.regions.update(region.id, form) : api.regions.create(form)),
    onSuccess: () => {
      toast.success(region ? "Region updated" : "Region created");
      void qc.invalidateQueries({ queryKey: ["regions"] });
      onClose();
    },
  });
  const fe = save.error instanceof ApiError ? save.error.fieldErrors : {};
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={region ? `Edit ${region.name}` : "New region"}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!form.code || !form.name} onClick={() => save.mutate()}>Save</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Name" error={fe.name}>{(p) => <Input {...p} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bangladesh / BDIX" autoFocus />}</Field>
        <Field label="Code" hint="Short identifier used in server names, e.g. BD or US." error={fe.code}>
          {(p) => <Input {...p} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="BD" className="w-40 font-mono" />}
        </Field>
        <Field label="Location (optional)" error={fe.location}>{(p) => <Input {...p} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Dhaka data centre" />}</Field>
        <Field label="Status" hint="Disabled regions accept no new servers.">
          {(p) => (
            <Select {...p} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as "ACTIVE" | "DISABLED" })} className="w-40">
              <option value="ACTIVE">Active</option>
              <option value="DISABLED">Disabled</option>
            </Select>
          )}
        </Field>
        {save.error && !Object.keys(fe).length ? <FormError error={save.error} /> : null}
      </div>
    </Dialog>
  );
}
